import DOMPurify from "dompurify";
import moment from "moment";
import React, { FC } from "react";
import {
  LuChevronDown,
  LuChevronUp,
  LuFile,
  LuForward,
  LuMail,
  LuPaperclip,
  LuReply,
  LuReplyAll,
  LuSend,
} from "react-icons/lu";

import { Avatar } from "../../../../components/newUI/avatar";
import { Button } from "../../../../components/newUI/button";
import { Email } from "../../../../factories/emails/models/emails.models";

type ContactEmailsThreadProps = {
  emails: Email[];
  expandedEmailIds: Record<string, boolean>;
  folderSelect?: React.ReactNode;
  onToggleEmail: (emailId: string) => void;
  onToggleAll: () => void;
  onCompose: () => void;
  onReply: (email: Email) => void;
  onReplyAll: (email: Email) => void;
  onForward: (email: Email) => void;
};

type ContactEmailThreadCardProps = {
  email: Email;
  expanded: boolean;
  onToggle: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
};

const getSenderName = (email: Email) =>
  email.from?.[0]?.name || email.from?.[0]?.email || "Unknown sender";

const getRecipients = (email: Email) =>
  email.to?.length
    ? `to ${email.to
        .slice(0, 2)
        .map((item) => item.name || item.email)
        .filter(Boolean)
        .join(", ")}${email.to.length > 2 ? ` +${email.to.length - 2}` : ""}`
    : "";

const getEmailAddress = (email: Email) =>
  email.from?.[0]?.email ? `<${email.from[0].email}>` : "";

const getSafeBody = (email: Email) =>
  DOMPurify.sanitize(email.body || email.snippet || "", {
    USE_PROFILES: { html: true },
  });

const getEmailSnippet = (email: Email) =>
  (email.snippet || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ContactEmailThreadCard: FC<ContactEmailThreadCardProps> = ({
  email,
  expanded,
  onToggle,
  onReply,
  onReplyAll,
  onForward,
}) => {
  const senderName = getSenderName(email);
  const recipients = getRecipients(email);
  const hasAttachments = !!email.attachments?.length;
  const snippet = getEmailSnippet(email);

  return (
    <div
      className={
        "overflow-hidden rounded-xl border border-[#E6E8EE] bg-white font-Manrope"
      }>
      <div
        className={
          "flex cursor-pointer items-center gap-3 px-4 py-[13px] transition hover:bg-[#FAFAFC]"
        }
        onClick={onToggle}>
        <Avatar
          id={email.id}
          fullName={senderName}
          size={"sm"}
          color={"primary"}
          avatarClassNames={"h-[30px] w-[30px] shrink-0 text-[11px] font-bold"}
        />
        <div className={"min-w-0 flex-1"}>
          <div className={"flex flex-wrap items-baseline gap-2"}>
            <span className={"text-sm font-bold text-[#1F2433]"}>
              {senderName}
            </span>
            {!!recipients && (
              <span className={"text-xs text-[#9097A8]"}>{recipients}</span>
            )}
          </div>
          {expanded ? (
            <div className={"mt-px text-[12.5px] text-[#9097A8]"}>
              {getEmailAddress(email)}
            </div>
          ) : (
            <>
              <div
                className={
                  "mt-px truncate text-[13px] font-semibold text-[#3A4256]"
                }>
                {email.subject || "No subject"}
              </div>
              {!!snippet && (
                <div className={"mt-px truncate text-[12.5px] text-[#9CA2B0]"}>
                  {snippet}
                </div>
              )}
            </>
          )}
        </div>
        {hasAttachments && (
          <LuPaperclip size={15} className={"shrink-0 text-[#B6BCC8]"} />
        )}
        <span className={"shrink-0 whitespace-nowrap text-xs text-[#B6BCC8]"}>
          {email.date ? moment.unix(email.date).format("MMM DD") : ""}
        </span>
        {expanded ? (
          <LuChevronUp size={17} className={"shrink-0 text-[#C2C7D2]"} />
        ) : (
          <LuChevronDown size={17} className={"shrink-0 text-[#C2C7D2]"} />
        )}
      </div>
      {expanded && (
        <div className={"px-[18px] pb-[18px] pl-16 pt-0"}>
          <div className={"mb-[9px] text-[14.5px] font-bold text-[#1F2433]"}>
            {email.subject || "No subject"}
          </div>
          <div
            className={
              "rich-text-content max-h-[62vh] max-w-full overflow-x-auto overflow-y-auto rounded-lg border border-[#F0F1F4] bg-white p-3 text-sm leading-[1.7] text-[#3A4256] [overflow-wrap:anywhere] [&_*]:max-w-full [&_img]:h-auto [&_img]:max-h-[520px] [&_pre]:whitespace-pre-wrap [&_table]:max-w-full"
            }
            dangerouslySetInnerHTML={{ __html: getSafeBody(email) }}
          />
          {hasAttachments && (
            <div className={"mt-[15px] border-t border-[#F0F1F4] pt-3.5"}>
              <div
                className={
                  "mb-[9px] text-[11.5px] font-bold uppercase tracking-[0.4px] text-[#9097A8]"
                }>
                Attachments
              </div>
              <div className={"flex flex-wrap gap-[9px]"}>
                {email.attachments?.map((attachment) => (
                  <span
                    key={attachment.id || attachment.content_id}
                    className={
                      "inline-flex items-center gap-2 rounded-[9px] border border-[#E2E5EC] bg-white px-3 py-2 text-[13px] font-semibold text-[#3A4256]"
                    }>
                    <LuFile size={15} className={"text-[#6B4EFF]"} />
                    {attachment.filename}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div
            className={
              "mt-4 flex flex-wrap gap-2.5 border-t border-[#F0F1F4] pt-3.5"
            }>
            <Button
              type={"button"}
              variant={"outline"}
              size={"sm"}
              autoWidth
              leftIcon={<LuReply size={15} />}
              className={"h-9 px-4 text-[13px] font-bold"}
              onClick={onReply}>
              Reply
            </Button>
            <Button
              type={"button"}
              variant={"outline"}
              size={"sm"}
              autoWidth
              leftIcon={<LuReplyAll size={15} />}
              className={"h-9 px-4 text-[13px] font-bold"}
              onClick={onReplyAll}>
              Reply all
            </Button>
            <Button
              type={"button"}
              variant={"outline"}
              size={"sm"}
              autoWidth
              leftIcon={<LuForward size={15} />}
              className={"h-9 px-4 text-[13px] font-bold"}
              onClick={onForward}>
              Forward
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ContactEmailsThread: FC<ContactEmailsThreadProps> = ({
  emails,
  expandedEmailIds,
  folderSelect,
  onToggleEmail,
  onToggleAll,
  onCompose,
  onReply,
  onReplyAll,
  onForward,
}) => {
  const allExpanded =
    emails.length > 0 && emails.every((email) => expandedEmailIds[email.id]);
  const emailCountLabel = `${emails.length} ${
    emails.length === 1 ? "email" : "emails"
  }`;

  return (
    <div className={"max-w-[860px] font-Manrope"}>
      <div
        className={
          "mb-[18px] flex flex-wrap items-center justify-between gap-4"
        }>
        <div className={"flex min-w-0 flex-wrap items-center gap-2.5"}>
          <div className={"flex items-center gap-2.5"}>
            <span className={"text-[13px] text-[#9097A8]"}>
              {emailCountLabel}
            </span>
            <Button
              type={"button"}
              variant={"outline"}
              size={"sm"}
              autoWidth
              leftIcon={
                allExpanded ? (
                  <LuChevronUp size={14} />
                ) : (
                  <LuChevronDown size={14} />
                )
              }
              className={
                "h-[30px] rounded-lg border-[#E2E4EA] px-3 text-[12.5px] font-semibold text-[#5F6577]"
              }
              onClick={onToggleAll}>
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          </div>
        </div>
        <div className={"flex flex-wrap items-center justify-end gap-2.5"}>
          {folderSelect}
          <Button
            type={"button"}
            variant={"primary"}
            size={"md"}
            autoWidth
            leftIcon={<LuSend size={16} />}
            className={"h-10 rounded-[9px] px-[18px] text-[13.5px] font-bold"}
            onClick={onCompose}>
            Compose email
          </Button>
        </div>
      </div>
      <div className={"flex flex-col gap-2.5"}>
        {emails.map((email) => (
          <ContactEmailThreadCard
            key={email.id}
            email={email}
            expanded={!!expandedEmailIds[email.id]}
            onToggle={() => onToggleEmail(email.id)}
            onReply={() => onReply(email)}
            onReplyAll={() => onReplyAll(email)}
            onForward={() => onForward(email)}
          />
        ))}
      </div>
    </div>
  );
};

type ContactEmailsNoMessagesProps = {
  onCompose: () => void;
  description?: string;
  folderSelect?: React.ReactNode;
  title?: string;
};

export const ContactEmailsNoMessages: FC<ContactEmailsNoMessagesProps> = ({
  description = "There are no emails with this client yet. New messages will appear here automatically.",
  folderSelect,
  onCompose,
  title = "No emails yet",
}) => (
  <div className={"max-w-[860px] font-Manrope"}>
    {!!folderSelect && (
      <div className={"mb-[18px] flex justify-end"}>{folderSelect}</div>
    )}
    <div
      className={
        "flex flex-col items-center gap-4 rounded-xl border border-[#E8E8EC] bg-white px-6 py-14 text-center"
      }>
      <span
        className={
          "inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#F1EEFC] text-[#8A73F0]"
        }>
        <LuMail size={28} />
      </span>
      <span className={"flex max-w-[380px] flex-col gap-1.5"}>
        <span className={"text-[17px] font-bold text-[#3A4256]"}>{title}</span>
        <span
          className={"text-[13.5px] font-medium leading-[1.55] text-[#8A92A3]"}>
          {description}
        </span>
      </span>
      <Button
        type={"button"}
        variant={"primary"}
        size={"md"}
        autoWidth
        leftIcon={<LuSend size={16} />}
        className={"h-10 rounded-[9px] px-[18px] text-[13.5px] font-bold"}
        onClick={onCompose}>
        Compose email
      </Button>
    </div>
  </div>
);
